<script lang="ts" setup>
import type { BlockSerializedChild } from '#layers/block-engine/public';
import type { GridLayoutData } from '#layers/blocks/composables/grid/grid-types';
import type { ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, ref, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { isBlockSerialized, isBlockSerializedChild, processAddBlock, useBlockAdapter, useBlockContext } from '#layers/block-engine/public';
import { useDetailPageContext } from '#layers/blocks/composables/block-context/use-detail-page-context';
import { GRID_CONFIG } from '#layers/blocks/composables/grid/grid-types';
import { getCustomizableFormFields } from '#layers/blocks/utils/form-fields';
import { getFieldLabel } from '#layers/data-model/public';
import { useContentPageCustomization } from '#layers/spaces/public';
// TODO permission check to only show this when the user can actually edit the form
import { TeleportPageHeaderActions, useToastResultHandling } from '#layers/ui/public';

const { collection } = defineProps<{
	collection: ResolvedMonospaceCollection;
}>();

const { t } = useI18n();
const { saveBlockSerialized } = useBlockAdapter();
const { editContentDetailPage } = useContentPageCustomization();
const { onError } = useToastResultHandling({
	error: {
		title: t('common.save_failed'),
	},
});

const { getChild, getRootBlock } = useBlockContext();
const detailPageContext = useDetailPageContext();
const addingFieldId = ref<string | null>(null);

const renderedFieldIds = computed(() => {
	const child = getChild();
	const rootBlock = getRootBlock();

	if (!isBlockSerialized(rootBlock)) {
		return new Set<string>();
	}

	return new Set(
		(child.slots.default ?? [])
			.map((childId) => rootBlock.children?.[childId])
			.filter((slotChild): slotChild is BlockSerializedChild =>
				isBlockSerializedChild(slotChild) && slotChild.blockId === '$builtin:FormField' && !!slotChild.props?.fieldId,
			)
			.map((slotChild) => slotChild.props.fieldId as string),
	);
});

const missingFields = computed(() =>
	getCustomizableFormFields(collection).filter((field) => !renderedFieldIds.value.has(field.id)),
);

const hasMissingFields = computed(() => missingFields.value.length > 0);

const triggerLabel = computed(() =>
	t('blocks.form.missing_fields.count', { count: missingFields.value.length }),
);

/**
 * Restore a missing field to the form.
 *
 * When grid layout is enabled, append the field in a new full-width row.
 * Save the added field and its grid placement together in one customization update.
 */
async function addMissingField(fieldId: string) {
	if (addingFieldId.value) {
		return;
	}

	const rootBlock = getRootBlock();
	const child = getChild();

	if (!isBlockSerialized(rootBlock)) {
		return;
	}

	addingFieldId.value = fieldId;

	try {
		const updatedBlock = structuredClone(toRaw(rootBlock));
		const { data, newId } = processAddBlock({
			editedBlock: updatedBlock,
			parentChildId: child.id,
			slot: 'default',
			blockId: '$builtin:FormField',
			props: {
				fieldId,
			},
		});

		Object.assign(updatedBlock, data);
		const updatedForm = updatedBlock.children?.[child.id];

		if (isBlockSerializedChild(updatedForm) && updatedForm.props.layoutGridData) {
			const gridData = updatedForm.props.layoutGridData as GridLayoutData;

			// Restore has no placement target; append a readable field without filling authored gaps.
			gridData.blockPositions[newId] = {
				rowIndex: gridData.rows.length,
				colStart: 0,
				colSpan: GRID_CONFIG.COLUMNS,
			};

			gridData.rows.push({ columns: GRID_CONFIG.COLUMNS });
		}

		await saveBlockSerialized(updatedBlock);
	}
	catch (error) {
		onError(error);
	}
	finally {
		addingFieldId.value = null;
	}
}

async function editForm() {
	await editContentDetailPage(collection.id, detailPageContext.pageId);
}
</script>

<template>
	<TeleportPageHeaderActions v-if="hasMissingFields">
		<DPopover
			class="ms-auto"
			:content="{ side: 'bottom', align: 'end' }"
			:ui="{ content: 'w-80 p-3' }"
		>
			<DButton
				data-testid="missing-fields-trigger"
				icon="lucide:triangle-alert"
				color="warning"
				variant="subtle"
			>
				{{ triggerLabel }}
			</DButton>

			<template #content>
				<div class="flex flex-col gap-3">
					<p class="text-xs text-muted">
						{{ $t('blocks.form.missing_fields.description') }}
					</p>

					<ul class="flex flex-col gap-1">
						<li
							v-for="field in missingFields"
							:key="field.id"
							data-testid="missing-field-item"
							class="flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5"
						>
							<span class="truncate text-sm">
								{{ getFieldLabel(field) }}
							</span>

							<DButton
								:data-testid="`missing-field-add-${field.id}`"
								variant="ghost"
								color="neutral"
								size="xs"
								:label="$t('blocks.form.missing_fields.add_to_form')"
								class="shrink-0"
								:loading="addingFieldId === field.id"
								:disabled="addingFieldId !== null"
								@click="addMissingField(field.id)"
							/>
						</li>
					</ul>

					<DButton
						data-testid="edit-form-button"
						variant="outline"
						color="neutral"
						icon="lucide:pencil"
						:label="$t('blocks.form.missing_fields.edit_form')"
						block
						@click="editForm"
					/>
				</div>
			</template>
		</DPopover>
	</TeleportPageHeaderActions>
</template>
